import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

// MD3 surface-container card: 24px radius, tonal surface (no border by
// default — separation comes from the Surface Container tone against the
// page's Surface background, not a hairline), shadow-sm at rest.
// `hoverEffect` bumps to shadow-md + a gentle scale for clickable cards
// (e.g. a search-result CardGrid tile).
const cardVariants = cva(
  'bg-card relative flex flex-col gap-6 rounded-3xl py-6 text-card-foreground shadow-sm transition-all duration-300 ease-emphasized-decelerate',
  {
    variants: {
      hoverEffect: {
        true: 'hover:shadow-md motion-safe:hover:scale-[1.02]',
        false: '',
      },
    },
    defaultVariants: {
      hoverEffect: false,
    },
  }
);

function Card({
  className,
  hoverEffect,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ hoverEffect, className }))}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex flex-col gap-1.5 px-6', className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('text-base leading-none font-medium', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-6', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
